<template>
  <div id="app">
    <header v-if="showHeader" class="app-header">
      <div class="app-header__content">
        <div class="app-header__logo">{{ $t('app.name') }}</div>
        <nav v-if="isAuthenticated" class="app-header__nav">
          <router-link to="/home" class="nav-link">{{ $t('navigation.dashboard') }}</router-link>
          <router-link to="/projects" class="nav-link">{{ $t('navigation.projects') }}</router-link>
          <router-link to="/reports/summary" class="nav-link">{{ $t('navigation.reports') }}</router-link>
          <router-link v-if="user?.role === 'admin'" to="/admin/users" class="nav-link">
            {{ $t('navigation.admin') }}
          </router-link>
          <router-link v-else-if="user?.role === 'supervisor'" to="/supervisor/users" class="nav-link">
            {{ $t('navigation.supervisor') }}
          </router-link>
        </nav>
      </div>
      <div class="app-header__actions">
        <span v-if="isAuthenticated" class="user-info">
          <span class="user-info__name">{{ user?.name }}</span>
          <span class="user-info__role">{{ user?.role }}</span>
        </span>
        <div v-if="isAuthenticated" class="header-menu">
          <button class="header-menu-item theme-toggle" :title="$t('settings.theme')" @click="toggleTheme">
            {{ theme === 'dark' ? '☀️' : '🌙' }}
          </button>
          <button class="header-menu-item locale-toggle" :title="$t('settings.language')" @click="toggleLocale">
            {{ currentLocale === 'en' ? 'AR' : 'EN' }}
          </button>
          <router-link to="/profile" class="header-menu-item" :title="$t('navigation.profile')">
            👤
          </router-link>
          <router-link to="/settings" class="header-menu-item" :title="$t('navigation.settings')">
            ⚙️
          </router-link>
        </div>
        <Button v-if="isAuthenticated" variant="outline" size="sm" @click="logoutAndGoToLogin">
          {{ $t('auth.logout') }}
        </Button>
        <Button v-else tag="router-link" :to="{ name: 'login' }" variant="primary" size="sm">
          {{ $t('auth.login') }}
        </Button>
      </div>
    </header>
    <main class="app-main">
      <router-view />
    </main>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuth } from './composables/useAuth';
import { useTheme } from './composables/useTheme';
import Button from './components/ui/Button.vue';

const route = useRoute();
const router = useRouter();
const { isAuthenticated, user, logout } = useAuth();
const { locale } = useI18n();
const { theme, toggleTheme, setTheme } = useTheme();

const currentLocale = computed(() => locale.value);

// Hide header on root, welcome, login, register, forgot-password, and terms screens
const showHeader = computed(() => {
  const hideHeaderRoutes = ['root', 'welcome', 'login', 'register', 'forgot-password', 'terms-and-conditions'];
  return !hideHeaderRoutes.includes(route.name);
});

const logoutAndGoToLogin = () => {
  logout();
  router.push({ name: 'root' });
};

const toggleLocale = () => {
  const newLocale = locale.value === 'en' ? 'ar' : 'en';
  locale.value = newLocale;
  localStorage.setItem('locale', newLocale);
  document.documentElement.lang = newLocale;
  document.documentElement.dir = newLocale === 'ar' ? 'rtl' : 'ltr';
};

// Initialize locale on mount
onMounted(() => {
  const savedLocale = localStorage.getItem('locale') || 'en';
  locale.value = savedLocale;
  document.documentElement.lang = savedLocale;
  document.documentElement.dir = savedLocale === 'ar' ? 'rtl' : 'ltr';

  const savedTheme = localStorage.getItem('theme') || 'light';
  setTheme(savedTheme);
});
</script>

<style lang="scss">
@import './styles/design-system.scss';

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-secondary);
  transition: background-color $transition-base;
}

.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $spacing-4 $spacing-6;
  background: var(--header-bg);
  color: var(--header-text);
  box-shadow: var(--shadow-lg);
  border-bottom: 1px solid var(--header-border);
  position: sticky;
  top: 0;
  z-index: $z-sticky;
  flex-wrap: wrap;
  gap: $spacing-4;
  transition: background $transition-base, color $transition-base, border-color $transition-base;

  @media (max-width: $breakpoint-md) {
    padding: $spacing-3 $spacing-4;
  }

  &__content {
    display: flex;
    align-items: center;
    gap: $spacing-8;
    flex: 1;
    min-width: 0;

    @media (max-width: $breakpoint-md) {
      gap: $spacing-4;
    }
  }

  &__logo {
    font-weight: $font-weight-bold;
    font-size: $font-size-xl;
    letter-spacing: 0.02em;
    background: linear-gradient(135deg, $primary-400 0%, $primary-600 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    white-space: nowrap;

    @media (max-width: $breakpoint-sm) {
      font-size: $font-size-lg;
    }
  }

  &__nav {
    display: flex;
    gap: $spacing-3;
    flex-wrap: wrap;

    @media (max-width: $breakpoint-md) {
      display: none;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: $spacing-4;
    flex-wrap: wrap;

    @media (max-width: $breakpoint-sm) {
      gap: $spacing-2;
    }
  }
}

.header-menu {
  display: flex;
  align-items: center;
  gap: $spacing-2;
}

.header-menu-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  text-decoration: none;
  font-size: 1.2rem;
  transition: all $transition-base;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  cursor: pointer;
  color: inherit;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }

  &.router-link-exact-active {
    background: rgba(255, 255, 255, 0.3);
  }

  &.theme-toggle,
  &.locale-toggle {
    font-size: 1rem;
    font-weight: $font-weight-medium;
  }
}

.nav-link {
  color: var(--text-secondary);
  text-decoration: none;
  font-weight: $font-weight-medium;
  font-size: $font-size-sm;
  padding: $spacing-2 $spacing-4;
  border-radius: $radius-md;
  transition: all $transition-base;
  position: relative;

  &:hover {
    color: var(--header-text);
    background: rgba(255, 255, 255, 0.1);
  }

  &.router-link-exact-active {
    color: var(--header-text);
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.24), rgba(59, 130, 246, 0.4));
    box-shadow: 0 8px 16px rgba(37, 99, 235, 0.35);
    transform: translateY(-1px);

    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 50%;
      transform: translateX(-50%);
      width: 90%;
      height: 3px;
      background: linear-gradient(90deg, $primary-400, $primary-600);
      border-radius: $radius-full;
    }
  }
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: $spacing-1;

  @media (max-width: $breakpoint-sm) {
    display: none;
  }

  &__name {
    font-size: $font-size-sm;
    font-weight: $font-weight-medium;
    color: var(--header-text);
  }

  &__role {
    font-size: $font-size-xs;
    color: var(--text-secondary);
    text-transform: capitalize;
  }
}

.app-main {
  flex: 1;
  padding: $spacing-6;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: $breakpoint-md) {
    padding: $spacing-4;
  }

  @media (max-width: $breakpoint-sm) {
    padding: $spacing-3;
  }
}
</style>
